import type { ComponentProps } from 'react'
import type { InlineWidget } from 'react-calendly'
import { z } from 'zod'
import { isClientSide } from './is-client-side'

const STORAGE_KEY = 'nwwp_schedule_prefill'

export type CalendlyPrefill = ComponentProps<typeof InlineWidget>['prefill']

const schedulePrefillSchema = z.object({
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
  phone: z.string().optional(),
})

export type SchedulePrefill = z.infer<typeof schedulePrefillSchema>

export function saveSchedulePrefill(prefill: SchedulePrefill): void {
  if (!isClientSide()) return
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(prefill))
  } catch {
    // Storage can be blocked (private mode, quota, disabled) — prefill is a
    // nice-to-have, so continue the flow without it rather than failing submit.
  }
}

export function clearSchedulePrefill(): void {
  if (!isClientSide()) return
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // See saveSchedulePrefill — storage access can throw; nothing to recover.
  }
}

function readSchedulePrefill(): SchedulePrefill | undefined {
  let raw: string | null
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY)
  } catch {
    return undefined
  }
  if (!raw) return undefined

  let stored: unknown
  try {
    stored = JSON.parse(raw)
  } catch {
    return undefined
  }
  const parsed = schedulePrefillSchema.safeParse(stored)
  if (!parsed.success) return undefined

  return parsed.data
}

export function loadCalendlyPrefill(): CalendlyPrefill {
  if (!isClientSide()) return undefined
  const data = readSchedulePrefill()
  if (!data) return undefined
  return {
    name: `${data.first_name} ${data.last_name}`,
    email: data.email,
    ...(data.phone && { location: data.phone }),
  }
}

export function loadScheduleFormPrefill(): SchedulePrefill | undefined {
  if (!isClientSide()) return undefined
  return readSchedulePrefill()
}
